import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router';
import { useGetUserSocialLinksQuery } from 'store/services/profile/profileApi';
import { PLATFORM_ICON_MAP, PLATFORM_LABEL_MAP } from 'utils/socialPlatformDetect';
import { SocialPlatform } from 'types/interfaces/socialLinks';
import { ExternalLinkModal, shouldSkipExternalLinkConfirm } from 'components/ExternalLinkModal/ExternalLinkModal';
import { SocialLinksModal } from 'components/SocialLinksModal/SocialLinksModal';
import { AssetUrls } from 'config/assets';
import './SocialLinksSection.css';

interface SocialLinksSectionProps {
  username: string;
  isOwnProfile: boolean;
  isViewerElite: boolean;
}

export const SocialLinksSection: React.FC<SocialLinksSectionProps> = ({
  username,
  isOwnProfile,
  isViewerElite,
}) => {
  const { data } = useGetUserSocialLinksQuery(username);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);

  const links = data?.links ?? [];

  const handleLinkClick = (e: React.MouseEvent, url: string) => {
    e.preventDefault();
    if (shouldSkipExternalLinkConfirm()) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else {
      setPendingUrl(url);
    }
  };

  const handleAddClick = () => {
    if (isViewerElite) {
      setIsEditModalOpen(true);
    } else {
      setShowUpsell((prev) => !prev);
    }
  };

  // Nothing to show for visitors on profiles with no links
  if (links.length === 0 && !isOwnProfile) return null;

  return (
    <>
      <div className="social-links-section">
        {links.map((link) => {
          const iconTuple = PLATFORM_ICON_MAP[link.platform as SocialPlatform] || PLATFORM_ICON_MAP.other;
          const label = PLATFORM_LABEL_MAP[link.platform as SocialPlatform] || link.platform;

          return (
            <a
              key={link.id}
              href={link.url}
              onClick={(e) => handleLinkClick(e, link.url)}
              className="social-links-section-icon"
              title={label}
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={iconTuple} />
            </a>
          );
        })}

        {isOwnProfile && (
          <div className="social-links-section-add-wrapper">
            <button
              className="social-links-section-add-btn"
              onClick={handleAddClick}
              title={isViewerElite ? 'Add social link' : 'Add social links with Elite'}
              aria-label="Add social link"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M7 1V13M1 7H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>

            {showUpsell && !isViewerElite && (
              <div className="social-links-section-upsell">
                <img
                  src={AssetUrls.icons.elite.badge}
                  alt="Elite"
                  className="social-links-section-upsell-icon"
                />
                <span>Add social links with</span>
                <Link to="/elite" className="social-links-section-upsell-link">
                  TradeMagic Elite
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      <ExternalLinkModal
        isOpen={pendingUrl !== null}
        url={pendingUrl ?? ''}
        onClose={() => setPendingUrl(null)}
      />

      <SocialLinksModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        username={username}
      />
    </>
  );
};

export default SocialLinksSection;
